import type { FC } from 'react';
import React, { useEffect, useRef } from 'react';

import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { ATTACHMENT_SIZE } from '@shared/const/attachment-size';
import { ModalNamesEnum } from '@shared/enums/modalNames.enum';
import { useAppDispatch } from '@shared/hooks/redux';
import useValidTypes from '@shared/hooks/useValidTypes';
import { isHeicFile } from '@shared/ui-kit/media/Attachemts/helpers/is-heic';
import { generateRandomNumericId } from '@shared/utils/generateRandomNumericId';
import modalObserver from '@shared/utils/observers/modalObserver';
import { uploadMediaFile, uploadMediaPhoto } from '@store/Common/commonThunk';

import UploadFileContainer from '../UploadFileContainer/UploadFileContainer';

import { AttachmentsItemCard } from './components/AttachmentsItemCard/AttachmentsItemCard';

import type { CropConfig, MediaType } from '@shared/models/media.model';
import type { AttachmentsProps } from 'shared/ui-kit';

const GALLERY_ITEM = {
  created_at: '',
  filename: '',
  token: '',
  original_filename: '',
  url: '',
  additional_info: {
    in_progress: true,
    size_urls: {
      big_icon: '',
      gallery: '',
      middle_icon: '',
    },
    sizes: [],
  },
  progress: 0,
  progressId: 0,
};

type AttachmentsContainerProps = {
  uploadFileButtonId?: string;
  uploadFileInputId?: string;
  fileNameLabelId?: string;
  loadingAttachment: MediaType[];
  cropTitle?: string;
  handleAddLoadingAttachment: (attachments: MediaType) => void;
  handleUpdateLoadingAttachment: (id: string, progress: number) => void;
  handleDeleteLoadingAttachment: (id: string) => void;
  onAddAsyncAttachment: (file: MediaType) => void;
  handleDeleteAttachment: (id: number, token: string, attachmentList: MediaType[]) => void;
  handleOpenViewModal(index?: number): void;
  cropConfig?: CropConfig | null;
};

export const AttachmentsContainer: FC<AttachmentsProps & AttachmentsContainerProps> = ({
  uploadFileButtonId,
  uploadFileInputId,
  fileNameLabelId,
  loadingAttachment,
  attachments,
  attachmentType = 'photo',
  entityType,
  entity_id,
  cropTitle,
  handleAddLoadingAttachment,
  handleUpdateLoadingAttachment,
  handleDeleteLoadingAttachment,
  onAddAsyncAttachment,
  attachmentCardsGridConfig = { xs: 12, sm: 6, md: 4, lg: 4 },
  attachmentCardsColumnSpacingConfig = { xs: 12, sm: 16, md: 24, lg: 24 },
  attachmentCardsRowSpacingConfig = { xs: 12, sm: 16, md: 24, lg: 24 },
  handleDeleteAttachment,
  permission = { isDelete: true, isDownload: true, isUpdate: false },
  maxAttachmentsLength = 5,
  handleOpenViewModal,
  heightImage,
  isCanAddMedia = true,
  isShowActionMenu = false,
  isCanAddMediaFromClipboard = true,
  isDisabledUploadFile = false,
  isShowCoverPhoto = false,
  handleChangeCoverPhoto,
  sxUploadContainer,
  sxAttachContainer,
  uploadSize = 'medium',
  isUploaderFullHeight,
  tooltipSettings = {
    color: 'dark',
    tooltipText: '',
    tooltipWithIcon: false,
  },
  showDeletePopupOnDelete,
  showItemCaption,
  isFullWidth = false,
  cropConfig = null,
  aspectRatio,
  isProtectedUrl = false,
  customAcceptedFormat,
}) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { validateMediaFormat, getAcceptedFormat } = useValidTypes();
  const { t } = useTranslation();
  const attachmentsLength = [...loadingAttachment, ...attachments].length;
  const deleteLoadingMedia = (id: string, file?: MediaType) => {
    handleDeleteLoadingAttachment(id);
    if (file !== undefined) {
      onAddAsyncAttachment(file);
    }
  };

  const handleSaveAttachments = (data: FormData, progressId: string, type: 'photo' | 'file' = 'photo') => {
    const attachedFile = data.get('file');
    if (attachedFile instanceof File) {
      const fileName = attachedFile.name;
      const fileSize = attachedFile.size;
      handleAddLoadingAttachment({
        ...GALLERY_ITEM,
        id: generateRandomNumericId(),
        size: fileSize,
        original_filename: fileName,
        progressId,
      });
    } else {
      return;
    }

    if (type === 'photo') {
      return dispatch(
        uploadMediaPhoto(
          data,
          (progress) => handleUpdateLoadingAttachment(progressId, progress),
          (file) => deleteLoadingMedia(progressId, file),
        ),
      );
    } else {
      return dispatch(
        uploadMediaFile(
          data,
          (progress) => handleUpdateLoadingAttachment(progressId, progress),
          (file) => deleteLoadingMedia(progressId, file),
        ),
      );
    }
  };

  const handleAddFile = async (attachment: any, isMultiple: boolean) => {
    if (!attachment) return;
    if (attachment.size > ATTACHMENT_SIZE) {
      NotificationService.error(t('mediaGallery.errors.fileWeight'));
      return;
    }

    if (await isHeicFile(attachment)) {
      attachment = new File([attachment], attachment.name, { type: 'image/heic' });
    }

    const extensionByName = attachment.name.substring(attachment.name.lastIndexOf('.')) || '';
    const loadingItemId = uuidv4() || '';
    if (attachmentType === 'photo' && !isMultiple) {
      if (!validateMediaFormat('photo', attachment.type || extensionByName)) {
        NotificationService.error(t('mediaGallery.errors.fileType'));
        return;
      }

      modalObserver.addModal(ModalNamesEnum.photoCrop, {
        props: {
          img: attachment,
          handleSavePhoto: handleSaveAttachments,
          loadingMediaId: loadingItemId,
          entityType: entityType,
          cropTitle: cropTitle || t('mediaGallery.crop.title'),
          entityId: entity_id,
          cropConfig,
        },
      });
    } else {
      if (!validateMediaFormat('file', attachment.type || extensionByName)) {
        NotificationService.error(t('mediaGallery.errors.fileType'));
        return;
      }

      const formData = new FormData();
      formData.append('file', attachment);
      formData.append('entity_type', entityType || '');
      formData.append('entity_id', entity_id ? entity_id.toString() : '');
      handleSaveAttachments(formData, loadingItemId, attachmentType);
    }
  };

  const iterationFiles = (files: File[]) => {
    const isMultiple = files.length > 1;
    for (let i = 0; i < files.length; i++) {
      if (attachmentsLength + i >= maxAttachmentsLength) {
        NotificationService.error(t('mediaGallery.errors.maximumNumberOfFile'));
        break;
      }

      const file = files[i];
      handleAddFile(file, isMultiple);
    }
  };
  const handleGetDataFromInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      const files = Array.from(e.target.files);
      iterationFiles(files);

      if (inputRef?.current?.value) {
        inputRef.current.value = '';
      }
    }
  };

  const handleGetDataFromDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer.files) {
      const files = Array.from(e.dataTransfer.files);
      iterationFiles(files);
    }
  };

  const handleGetMediaFromClipboard = (e: ClipboardEvent) => {
    if (e?.clipboardData?.files) {
      const files = Array.prototype.slice.call(e.clipboardData.files);
      iterationFiles(files);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getFile = (e: ClipboardEvent | any) => {
    if (isCanAddMedia && isCanAddMediaFromClipboard) {
      handleGetMediaFromClipboard(e);
    }
  };

  useEffect(() => {
    window.addEventListener('paste', getFile);
    return () => {
      window.removeEventListener('paste', getFile);
    };
  }, [getFile]);

  const onDeleteAttachment = (id: number, token: string) => {
    handleDeleteAttachment(id, token, attachments);
  };

  return (
    <Box>
      {!attachmentsLength && isCanAddMedia ? (
        <UploadFileContainer
          uploadFileButtonId={uploadFileButtonId}
          uploadFileInputId={uploadFileInputId}
          heightContainer={heightImage || 175}
          handleGetDataFromInputChange={handleGetDataFromInputChange}
          handleGetDataFromDrop={handleGetDataFromDrop}
          ref={inputRef}
          size={uploadSize}
          isMultipleInput
          acceptedFormat={customAcceptedFormat ?? getAcceptedFormat(attachmentType)}
          isDisabledUploadFile={isDisabledUploadFile}
          sxUploadContainer={sxUploadContainer}
          tooltipSettings={tooltipSettings}
          isFullHeight={isUploaderFullHeight}
          isFullWidth={isFullWidth}
          aspectRatio={aspectRatio}
        />
      ) : (
        <></>
      )}

      {attachmentsLength ? (
        <Grid
          container
          columnSpacing={{
            xs: `${attachmentCardsColumnSpacingConfig.xs}px`,
            sm: `${attachmentCardsColumnSpacingConfig.sm}px`,
            md: `${attachmentCardsColumnSpacingConfig.md}px`,
            lg: `${attachmentCardsColumnSpacingConfig.lg}px`,
          }}
          rowSpacing={{
            xs: `${attachmentCardsRowSpacingConfig.xs}px`,
            sm: `${attachmentCardsRowSpacingConfig.sm}px`,
            md: `${attachmentCardsRowSpacingConfig.md}px`,
            lg: `${attachmentCardsRowSpacingConfig.lg}px`,
          }}
          alignItems="start"
        >
          <Grid
            xs={attachmentCardsGridConfig.xs}
            sm={attachmentCardsGridConfig.sm}
            md={attachmentCardsGridConfig.md}
            lg={attachmentCardsGridConfig.lg}
            xl={attachmentCardsGridConfig?.xl}
            sx={{
              display:
                maxAttachmentsLength > attachmentsLength + loadingAttachment.length && isCanAddMedia
                  ? 'block'
                  : 'none',
            }}
            item
          >
            <UploadFileContainer
              heightContainer={heightImage}
              handleGetDataFromInputChange={handleGetDataFromInputChange}
              handleGetDataFromDrop={handleGetDataFromDrop}
              ref={inputRef}
              isMultipleInput
              size="small"
              isFullWidth
              acceptedFormat={getAcceptedFormat(attachmentType)}
              isDisabledUploadFile={isDisabledUploadFile}
              sxUploadContainer={sxUploadContainer}
              tooltipSettings={tooltipSettings}
              aspectRatio={aspectRatio}
            />
          </Grid>

          {[...loadingAttachment, ...attachments].map((attachmentsItem, index) => (
            <Grid
              key={attachmentsItem.id}
              xs={attachmentCardsGridConfig.xs}
              sm={attachmentCardsGridConfig.sm}
              md={attachmentCardsGridConfig.md}
              lg={attachmentCardsGridConfig.lg}
              xl={attachmentCardsGridConfig?.xl}
              item
            >
              <AttachmentsItemCard
                key={attachmentsItem.id}
                fileNameLabelId={fileNameLabelId}
                heightImage={heightImage}
                onDeleteAttachment={onDeleteAttachment}
                handleOpenViewModal={handleOpenViewModal}
                index={index - loadingAttachment.length}
                isCanDelete={permission?.isDelete}
                attachmentsItem={attachmentsItem}
                isShowActionMenu={isShowActionMenu}
                isShowCoverPhoto={isShowCoverPhoto}
                handleChangeCoverPhoto={handleChangeCoverPhoto}
                sxAttachContainer={sxAttachContainer}
                showDeletePopupOnDelete={showDeletePopupOnDelete}
                attachmentType={attachmentType}
                showItemCaption={showItemCaption}
                aspectRatio={aspectRatio}
                isProtectedUrl={isProtectedUrl}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <></>
      )}
    </Box>
  );
};
